import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ImageOff, X } from "lucide-react";
import axios from "axios";
import { photoFull } from "@/lib/photoUrl";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Public, login-free photo wall of the market-survey products a colleague shared. */
export default function MarketSharedWall() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/market/share/public/${token}`)
      .then(({ data: d }) => setData(d))
      .catch((e) => setError(e.response?.status === 410
        ? "链接已过期 · This link has expired"
        : "链接无效或已作废 · Link not found or revoked"));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background text-foreground" data-testid="shared-wall-error">
        <ImageOff size={30} className="text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="shared-wall-page">
      <header className="border-b border-border px-5 sm:px-10 py-5 sticky top-0 bg-background/90 backdrop-blur z-20">
        <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">
          {data.title || "Market Product Survey"}
        </h1>
        <p className="text-xs text-muted-foreground mt-1 font-mono" data-testid="shared-wall-meta">
          {data.count} products · valid until {(data.expires_at || "").slice(0, 10)}
        </p>
      </header>

      <main className="px-5 sm:px-10 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
          {data.items.map((p) => {
            const first = p.photos?.[0];
            return (
              <figure key={p.id} className="group" data-testid={`shared-wall-item-${p.id}`}>
                <button
                  type="button"
                  onClick={() => first && setZoom(first.url)}
                  className="block w-full aspect-square overflow-hidden rounded-md border border-border bg-secondary/40"
                >
                  {first ? (
                    <img src={photoFull(first.thumb_url || first.url)} alt={p.description}
                      loading="lazy"
                      onError={(e) => {
                        // Thumb missing on R2 → fall back to the original, then give up.
                        if (first.thumb_url && !e.target.src.includes(first.url.split("/").pop())) {
                          e.target.src = photoFull(first.url);
                        } else {
                          e.target.style.display = "none";
                        }
                      }}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageOff size={18} /></div>
                  )}
                </button>
                <figcaption className="mt-2 space-y-0.5">
                  <p className="text-sm font-medium leading-snug line-clamp-2">{p.description || "-"}</p>
                  <p className="text-[11px] font-mono text-muted-foreground flex justify-between gap-2">
                    <span className="truncate">{p.store}</span>
                    <span className="shrink-0">{p.survey_date}</span>
                  </p>
                </figcaption>
              </figure>
            );
          })}
        </div>
      </main>

      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setZoom(null)} data-testid="shared-wall-lightbox">
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setZoom(null)}>
            <X size={26} />
          </button>
          <img src={photoFull(zoom)} alt="" className="max-h-[92vh] max-w-full object-contain" />
        </div>
      )}
    </div>
  );
}
